import { Button, Heading, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { PaymentMethodType } from '@afterpaytouch/portal-api'
import { useTranslation, useSubHeadingColor } from '../../utils/convergence'
import React, { useEffect, useState } from 'react'
import { DisabledModal, Head, ModuleContainer, NewPaymentMethod } from '../../../src/component'
import { Route, useSummaryRoute } from '../../../src/config/router'
import { checkoutErrors, CheckoutPage, TopazError } from '../../../src/model'
import { PageNames, PaymentMethodTypes, TrackingEvent } from '../../../src/model/monitoring'
import {
  resetApplePayError,
  resetCheckoutError,
  resetPreferredPaymentMethodErrors,
  resetRecoverableFlowError,
  setCheckoutError,
  setHasUserInitiatedChangedPaymentMethod,
  useAppDispatch,
  useAppRouter,
  useExperiment,
  useIsRecoverableFlow,
  useSelectedPaymentMethod,
} from '../../../src/state'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../../src/integrations/monitoring'

export const NewPaymentMethodPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const paymentMethod = useSelectedPaymentMethod()
  const { instrument } = useMonitoringWithEnduringEventProperties({
    paymentMethodType: PaymentMethodTypes[paymentMethod?.type as PaymentMethodType],
    pageName: PageNames.ADD_NEW_PAYMENT_METHOD,
  })
  const { headingSize, textSize } = useCheckoutSizes()
  const summaryRoute = useSummaryRoute()
  const isRecoverableFlow = useIsRecoverableFlow()

  // Hide cardholder name experiment
  const { control: isCardHolderNameField } = useExperiment('CheckoutHideCardHolderName')
  const addCardText = isCardHolderNameField ? t('paymentMethod:addACard') : t('paymentMethod:addACardExperiment')
  const pageTitle = `${t('paymentMethod:addNewPaymentMethod')} | ${t('common:brandName')}`
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const subHeadingColor = useSubHeadingColor()

  const handleSubmit = async (): Promise<void> => {
    if (isRecoverableFlow) {
      dispatch(resetRecoverableFlowError())
    } else {
      dispatch(resetCheckoutError())
    }

    dispatch(resetApplePayError())
    dispatch(resetPreferredPaymentMethodErrors())
    dispatch(setHasUserInitiatedChangedPaymentMethod())

    router.push(summaryRoute)
  }

  const handleTerminalError = (reason: TopazError): void => {
    dispatch(
      setCheckoutError({
        error: checkoutErrors[reason],
        errorCode: reason,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const saveAddNewPaymentMethod = instrument(TrackingEvent.PRESSED_ADD_NEW_PAYMENT_METHOD_SAVE_BUTTON, () => setFormSubmitted(!formSubmitted))

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.PAYMENT_METHOD)
  }, [])

  const renderSubtext = (): React.ReactNode => {
    const textContent = (
      <Text size={textSize} color={subHeadingColor}>
        {addCardText}
      </Text>
    )

    return isArcadeDesignLanguage ? (
      <ModuleContainer showBorder={false} padding='None' noBottomMargin>
        {textContent}
      </ModuleContainer>
    ) : (
      textContent
    )
  }

  return (
    <>
      <Head title={pageTitle} />
      {!isArcadeDesignLanguage && <Heading size={headingSize}>{t('paymentMethod:addNewPaymentMethod')}</Heading>}
      {!isArcadeDesignLanguage && renderSubtext()}
      <ModuleContainer padding={{ vertical: 'Small' }} mintBorder showBorder={!isArcadeDesignLanguage}>
        <NewPaymentMethod
          onSuccess={async () => await handleSubmit()}
          onSubmit={(isSubmitting) => setIsSubmitting(isSubmitting)}
          formSubmitted={formSubmitted}
          onTerminalError={handleTerminalError}
          displayCreditCardMessage={false}
        />
      </ModuleContainer>
      <Button loading={isSubmitting} padding='Fluid' disabled={isSubmitting} testNameSpace='new-payment-method' onClick={saveAddNewPaymentMethod}>
        {t(isArcadeDesignLanguage ? 'paymentMethod:savePaymentMethod' : 'paymentMethod:save')}
      </Button>
      <TrackPageViewed />
      {isSubmitting && <DisabledModal />}
    </>
  )
}
